import { useToast } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { ApiError, useApiClient } from "../../../apiClient";
import { translate } from "../../../i18n";
import {
    AdminBookingResponseDto,
    CreateAdminBookingDto,
} from "../../../models/payment/dto/admin-booking.dto";

export const useCreateAdminBooking = (
    onSuccess: (data: AdminBookingResponseDto) => void,
) => {
    const apiClient = useApiClient();
    const toast = useToast();
    return useMutation({
        mutationKey: ['createAdminBooking'],
        mutationFn: (body: CreateAdminBookingDto) =>
            apiClient.fetchApi<AdminBookingResponseDto, CreateAdminBookingDto>({
                method: 'POST',
                endpoint: 'payments/admin-bookings',
                body,
            }),
        onSuccess: (data) => onSuccess(data),
        onError: (err) => {
            // A 409 that names a pricing problem is NOT the slot-taken one.
            // Sent it through the generic conflict copy below and the operator
            // is told to pick another time, when the fix is a tariff row on
            // this one. Keyed on the forwarded `code`, not on message text.
            if (
                err instanceof ApiError &&
                err.status === 409 &&
                err.code === 'COMPED_BOOKING_NOT_JOINABLE'
            ) {
                toast({
                    title: 'Aucun prix pour cette capacité',
                    description: err.message,
                    status: 'warning',
                    duration: 8000,
                    position: 'top-right',
                    isClosable: true,
                });
                return;
            }
            // Specialize the 409 (overlap / slot taken) toast — operators
            // need to know the slot is busy, not a vague "error".
            if (err instanceof ApiError && err.status === 409) {
                toast({
                    title: 'Créneau déjà réservé',
                    description:
                        'Une autre réservation ou un blocage occupe ce créneau. Choisis un autre horaire.',
                    status: 'warning',
                    duration: 4000,
                    position: 'top-right',
                });
                return;
            }
            // Specialize 400 (validation, free-amount, etc.) so the operator
            // sees the API's explanatory message instead of the generic
            // "error" — these messages are written for the operator UX.
            //
            // class-validator can reject with an array of messages which
            // ApiError stringifies as "msg1,msg2,msg3". Detect that case and
            // render a bulleted multi-line description rather than a wall
            // of comma-separated text.
            if (err instanceof ApiError && err.status === 400) {
                const isMultiMessage = err.message.includes(',');
                const description = isMultiMessage
                    ? err.message
                          .split(',')
                          .map((s) => `• ${s.trim()}`)
                          .join('\n')
                    : undefined;
                toast({
                    title: isMultiMessage ? 'Données invalides' : err.message,
                    description,
                    status: 'warning',
                    duration: 5000,
                    position: 'top-right',
                    isClosable: true,
                });
                return;
            }
            toast({
                title: translate('error'),
                status: 'error',
                duration: 3000,
                position: 'top-right',
            });
        },
    });
};
